import { iconChevronDown, iconDownload } from "@kensho/icons";
import { Button, DropdownMenu } from "@kensho/neo";
import { use, useState } from "react";
import { useParams } from "react-router";

import { ErrorDialog } from "../../../components/error-dialog";
import { useDownloadTranscript } from "../../../hooks/use-download-transcript";
import { SiteAnalyticsContext } from "../../../providers/site-analytics-context";
import { useTranscriptContext } from "../../../providers/use-transcript-context";
import { ScribeError, TranscriptFormat } from "../../../types/types";
import { ErrorInput, parseError } from "../../../utils/parse-error";

/**
 * Trigger a download of the transcript in the specified format.
 * Downloaded file will be named <transcript_id>.<mime_type>
 */
export function DownloadTranscriptButton() {
  const [downloadError, setDownloadError] = useState<ScribeError>();
  const { transcriptId } = useParams();
  const { transcript } = useTranscriptContext();
  const [status, downloadTranscript] = useDownloadTranscript();
  const analytics = use(SiteAnalyticsContext);

  async function downloadFormat(format: TranscriptFormat): Promise<void> {
    analytics.sendEvent("download", { file_type: format });
    if (!transcriptId) {
      setDownloadError({ type: "transcriptNotFound" });
      return;
    }
    try {
      await downloadTranscript(transcriptId, format, {
        transcript,
      });
    } catch (error) {
      setDownloadError(parseError(error as ErrorInput));
    }
  }

  return (
    <>
      {downloadError && (
        <ErrorDialog
          isOpen
          error={downloadError}
          onClose={() => setDownloadError(undefined)}
        />
      )}
      <DropdownMenu
        items={[
          {
            id: "docx",
            label: "DOCX",
            onAction: () =>
              downloadFormat(
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
              ),
          },
          {
            id: "txt",
            label: "TXT",
            onAction: () => downloadFormat("text/plain"),
          },
          {
            id: "vtt",
            label: "VTT",
            onAction: () => downloadFormat("text/vtt"),
          },
          {
            id: "json",
            label: "JSON",
            onAction: () => downloadFormat("application/json"),
          },
        ]}
      >
        <Button
          disabled={!transcriptId || status === "pending"}
          icon={status === "pending" ? undefined : iconDownload}
          rightIcon={iconChevronDown}
          intent="primary"
        >
          {status === "pending" ? "Downloading…" : "Download"}
        </Button>
      </DropdownMenu>
    </>
  );
}
